import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, Button, IconButton, Chip, Switch,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Tooltip, Select, MenuItem, InputAdornment, LinearProgress,
  Checkbox, ListItemText
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SearchIcon from '@mui/icons-material/Search';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import adminAxios from '../../utils/adminAxios';

const ALCANCE_LABEL = { todos: 'Todos', categorias: 'Categorías' };

const dinero = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const num = (n) => Number(n || 0).toLocaleString('es-MX');

const StatCard = ({ label, valor, sub }) => (
  <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#94A3B8', textTransform: 'uppercase', mb: 1 }}>{label}</Typography>
    <Typography variant="h4" fontWeight={800} color="#0E2465" sx={{ lineHeight: 1.1 }}>{valor}</Typography>
    {sub && <Typography variant="caption" color="#94A3B8">{sub}</Typography>}
  </Paper>
);

// Fecha (Date) → 'YYYY-MM-DD' para el input date
const aInputDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const fmtFecha = (val) => {
  if (!val) return 'Sin vencimiento';
  const d = new Date(val);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const estadoChip = (p) => {
  if (p.vencido) return { label: 'Vencido', bg: '#FFE0DD', color: '#B23B33' };
  if (p.activo) return { label: 'Activo', bg: '#D6F3E8', color: '#1B7A5A' };
  return { label: 'Inactivo', bg: '#F1F5F9', color: '#737791' };
};

// Descuento con icono %/$ (porcentaje = azul, monto fijo = verde)
const DescuentoBadge = ({ p }) => {
  const esFijo = p.tipo === 'monto_fijo';
  const color = esFijo ? '#1B7A5A' : '#3B5BDB';
  const bg = esFijo ? '#D6F3E8' : '#EEF2FF';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
      <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>
        {esFijo ? '$' : '%'}
      </Box>
      <Typography fontWeight={800} color={color}>{esFijo ? dinero(p.montoFijo) : `${p.descuentoPct}%`}</Typography>
    </Box>
  );
};

// Barra de uso: usos / límite (∞ si no hay límite)
const UsosCelda = ({ p }) => {
  const limite = p.limiteUsos || 0;
  const usos = p.usos || 0;
  const pct = limite > 0 ? Math.min(100, Math.round((usos / limite) * 100)) : 0;
  const color = pct >= 100 ? '#E5484D' : pct >= 80 ? '#D9822B' : '#1B7A5A';
  return (
    <Box sx={{ minWidth: 140 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="caption" color="#0E2465" fontWeight={700}>
          {num(usos)} / {limite > 0 ? num(limite) : '∞'}
        </Typography>
        {limite > 0 && <Typography variant="caption" fontWeight={800} color={color}>{pct}%</Typography>}
      </Box>
      {limite > 0 && (
        <LinearProgress variant="determinate" value={pct}
          sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
      )}
    </Box>
  );
};

// Símbolo de adorno ($/%) con color explícito: MUI envolvería un string suelto en un
// Typography color="text.secondary", que en este tema es blanco (invisible sobre el campo).
const Simbolo = ({ children }) => (
  <Typography component="span" sx={{ color: '#475569', fontWeight: 700, fontSize: 14 }}>{children}</Typography>
);

const dialogVacio = {
  modo: 'crear', codigo: '', tipo: 'porcentaje', valor: '',
  montoMinimo: '', limiteUsos: '', alcance: 'todos', categorias: [], vigencia: '', descripcion: '', activo: true
};

const Promociones = () => {
  const [promos, setPromos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [categoriasLista, setCategoriasLista] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await adminAxios.get('/api/admin/promos');
      setPromos(res.data);
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudieron cargar los códigos.' });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    adminAxios.get('/api/category')
      .then(res => setCategoriasLista((res.data || []).filter(c => c.status !== false)))
      .catch(() => {});
  }, []);

  const catNombre = useCallback((id) => categoriasLista.find(c => c._id === id)?.name || '', [categoriasLista]);

  const stats = useMemo(() => {
    const total = promos.length;
    const activos = promos.filter(p => p.activo).length;
    return {
      total,
      activos,
      inactivos: promos.filter(p => !p.activo).length,
      vencidos: promos.filter(p => p.vencido).length,
      usos: promos.reduce((acc, p) => acc + (p.usos || 0), 0),
      pctActivos: total ? Math.round((activos / total) * 100) : 0
    };
  }, [promos]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return promos.filter(p => {
      const estado = p.vencido ? 'vencido' : p.activo ? 'activo' : 'inactivo';
      if (filtroEstado && estado !== filtroEstado) return false;
      if (filtroTipo && p.tipo !== filtroTipo) return false;
      if (q && !(`${p.codigo} ${p.descripcion || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [promos, busqueda, filtroEstado, filtroTipo]);

  const usosFiltrados = useMemo(() => filtrados.reduce((a, p) => a + (p.usos || 0), 0), [filtrados]);

  const abrirCrear = () => { setErrores({}); setDialog({ ...dialogVacio }); };
  const camposDe = (p) => ({
    codigo: p.codigo, tipo: p.tipo || 'porcentaje',
    valor: String(p.tipo === 'monto_fijo' ? p.montoFijo : p.descuentoPct),
    montoMinimo: p.montoMinimo ? String(p.montoMinimo) : '',
    limiteUsos: p.limiteUsos ? String(p.limiteUsos) : '',
    alcance: p.alcance || 'todos',
    categorias: (p.categorias || []).map(c => c._id || c),
    vigencia: aInputDate(p.vigencia),
    descripcion: p.descripcion || '', activo: p.activo
  });
  const abrirEditar = (p) => { setErrores({}); setDialog({ modo: 'editar', _id: p._id, ...camposDe(p) }); };
  const abrirDuplicar = (p) => { setErrores({}); setDialog({ modo: 'crear', ...camposDe(p), codigo: `${p.codigo}COPIA`, activo: true }); };

  const guardar = async () => {
    const d = dialog;
    const errs = {};
    if (!d.codigo.trim()) errs.codigo = 'El código es obligatorio';
    const v = Number(d.valor);
    if (d.tipo === 'porcentaje') {
      if (!Number.isFinite(v) || v < 1 || v > 100) errs.valor = 'Porcentaje entre 1 y 100';
    } else if (!Number.isFinite(v) || v <= 0) {
      errs.valor = 'Monto mayor a 0';
    }
    if (d.alcance === 'categorias' && (!d.categorias || d.categorias.length === 0)) {
      errs.alcance = 'Selecciona al menos una categoría';
    }
    if (Object.keys(errs).length) { setErrores(errs); return; }

    setGuardando(true);
    try {
      const payload = {
        codigo: d.codigo.trim().toUpperCase(),
        tipo: d.tipo,
        descuentoPct: d.tipo === 'porcentaje' ? v : 0,
        montoFijo: d.tipo === 'monto_fijo' ? v : 0,
        montoMinimo: Number(d.montoMinimo) || 0,
        limiteUsos: Number(d.limiteUsos) || 0,
        alcance: d.alcance,
        categorias: d.alcance === 'categorias' ? d.categorias : [],
        descripcion: d.descripcion.trim(),
        activo: d.activo,
        vigencia: d.vigencia || null
      };
      if (d.modo === 'crear') {
        const res = await adminAxios.post('/api/admin/promos', payload);
        setPromos(prev => [res.data, ...prev]);
        setToast({ tipo: 'success', msg: 'Código creado.' });
      } else {
        const res = await adminAxios.patch(`/api/admin/promos/${d._id}`, payload);
        setPromos(prev => prev.map(p => p._id === d._id ? res.data : p));
        setToast({ tipo: 'success', msg: 'Código actualizado.' });
      }
      setDialog(null);
    } catch (e) {
      setErrores({ codigo: e.response?.data?.msg || 'No se pudo guardar.' });
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (p) => {
    try {
      const res = await adminAxios.patch(`/api/admin/promos/${p._id}`, { activo: !p.activo });
      setPromos(prev => prev.map(x => x._id === p._id ? res.data : x));
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo cambiar el estado.' });
    }
  };

  const eliminar = async () => {
    const p = confirmDel;
    setConfirmDel(null);
    try {
      await adminAxios.delete(`/api/admin/promos/${p._id}`);
      setPromos(prev => prev.filter(x => x._id !== p._id));
      setToast({ tipo: 'success', msg: 'Código eliminado.' });
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo eliminar.' });
    }
  };

  const esFijo = dialog?.tipo === 'monto_fijo';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={abrirCrear}
          sx={{ bgcolor: 'primary.main', fontWeight: 700, borderRadius: 2, px: 2.5 }}>
          Nuevo código
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <StatCard label="Total códigos" valor={stats.total} sub="creados" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <StatCard label="Activos" valor={stats.activos} sub={`${stats.pctActivos}% del total`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <StatCard label="Inactivos" valor={stats.inactivos} sub="desactivados" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <StatCard label="Vencidos" valor={stats.vencidos} sub="expirados" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <StatCard label="Usos totales" valor={num(stats.usos)} sub="canjes registrados" />
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {/* Buscador + filtros */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 2.5, flexWrap: 'wrap', borderBottom: '1px solid #F1F5F9' }}>
          <TextField size="small" placeholder="Buscar código o descripción…" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8' }} /></InputAdornment> }} />
          <Select size="small" displayEmpty value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">Todos los estados</MenuItem>
            <MenuItem value="activo">Activo</MenuItem>
            <MenuItem value="inactivo">Inactivo</MenuItem>
            <MenuItem value="vencido">Vencido</MenuItem>
          </Select>
          <Select size="small" displayEmpty value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">Todos los tipos</MenuItem>
            <MenuItem value="porcentaje">Porcentaje</MenuItem>
            <MenuItem value="monto_fijo">Monto fijo</MenuItem>
          </Select>
          <Typography sx={{ ml: 'auto', fontSize: 13, color: '#94A3B8' }}>{filtrados.length} de {promos.length} códigos</Typography>
        </Box>

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : promos.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 6, color: '#94A3B8' }}>
            <LocalOfferOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
            <Typography>No hay códigos promocionales todavía.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: '#94A3B8', fontWeight: 700, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', borderBottom: '1px solid #F1F5F9' } }}>
                  <TableCell>Código</TableCell>
                  <TableCell align="center">Descuento</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Usos</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell>Alcance</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center">Activo</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map(p => {
                  const est = estadoChip(p);
                  return (
                    <TableRow key={p._id} hover sx={{ '& td': { borderBottom: '1px solid #F8FAFC' } }}>
                      <TableCell>
                        <Typography fontWeight={800} color="#0E2465">{p.codigo}</Typography>
                        {p.descripcion && (
                          <Typography variant="caption" color="#737791" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.descripcion}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center"><DescuentoBadge p={p} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#0E2465" fontWeight={600}>
                          {p.tipo === 'monto_fijo' ? 'Monto fijo' : 'Porcentaje'}
                        </Typography>
                        {p.montoMinimo > 0 && <Typography variant="caption" color="#94A3B8">Mín. {dinero(p.montoMinimo)}</Typography>}
                      </TableCell>
                      <TableCell><UsosCelda p={p} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EventOutlinedIcon sx={{ fontSize: 15, color: p.vencido ? '#B23B33' : '#94A3B8' }} />
                          <Typography variant="body2" color={p.vencido ? '#B23B33' : '#0E2465'} fontWeight={600}>
                            {fmtFecha(p.vigencia)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {p.alcance === 'categorias' ? (
                          <Box>
                            <Typography variant="body2" color="#0E2465" fontWeight={600}>Categorías</Typography>
                            <Tooltip title={(p.categorias || []).map(c => c.name).join(', ')}>
                              <Typography variant="caption" color="#94A3B8" sx={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {(p.categorias || []).map(c => c.name).join(', ') || '—'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="#475569">Todos</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={est.label} size="small" sx={{ bgcolor: est.bg, color: est.color, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={p.vencido ? 'Vencido: reactiva cambiando la vigencia' : (p.activo ? 'Desactivar' : 'Activar')}>
                          <span>
                            <Switch checked={p.activo} onChange={() => toggleActivo(p)} disabled={p.vencido} size="small" />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(p)} sx={{ color: '#7495F8' }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicar">
                          <IconButton size="small" onClick={() => abrirDuplicar(p)} sx={{ color: '#94A3B8', '&:hover': { color: '#0E2465' } }}>
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => setConfirmDel(p)} sx={{ color: '#CBD5E1', '&:hover': { color: '#B23B33' } }}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtrados.length === 0 && (
                  <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', color: '#94A3B8', py: 5 }}>
                    No hay códigos que coincidan con el filtro.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {promos.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderTop: '1px solid #F1F5F9', flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Mostrando {filtrados.length} resultado{filtrados.length === 1 ? '' : 's'}</Typography>
            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Usos totales filtrados: <strong style={{ color: '#0E2465' }}>{num(usosFiltrados)}</strong></Typography>
          </Box>
        )}
      </Paper>

      {/* Dialog crear/editar */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#0E2465' }}>
          {dialog?.modo === 'crear' ? 'Nuevo código promocional' : 'Editar código'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField label="Código" fullWidth size="small" value={dialog?.codigo || ''}
                onChange={(e) => setDialog(d => ({ ...d, codigo: e.target.value.toUpperCase() }))}
                error={!!errores.codigo} helperText={errores.codigo} placeholder="ONESERVICE20" />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Tipo de descuento</Typography>
              <Select fullWidth size="small" value={dialog?.tipo || 'porcentaje'}
                onChange={(e) => setDialog(d => ({ ...d, tipo: e.target.value }))}>
                <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
                <MenuItem value="monto_fijo">Monto fijo ($)</MenuItem>
              </Select>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>{esFijo ? 'Monto de descuento' : 'Descuento'}</Typography>
              <TextField fullWidth size="small" type="number" value={dialog?.valor || ''}
                onChange={(e) => setDialog(d => ({ ...d, valor: e.target.value }))}
                error={!!errores.valor} helperText={errores.valor}
                InputProps={esFijo
                  ? { startAdornment: <InputAdornment position="start"><Simbolo>$</Simbolo></InputAdornment> }
                  : { endAdornment: <InputAdornment position="end"><Simbolo>%</Simbolo></InputAdornment> }}
                inputProps={esFijo ? { min: 1 } : { min: 1, max: 100 }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Compra mínima (opcional)</Typography>
              <TextField fullWidth size="small" type="number" value={dialog?.montoMinimo || ''}
                onChange={(e) => setDialog(d => ({ ...d, montoMinimo: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><Simbolo>$</Simbolo></InputAdornment> }}
                helperText="0 o vacío = sin mínimo" inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Límite de usos (opcional)</Typography>
              <TextField fullWidth size="small" type="number" value={dialog?.limiteUsos || ''}
                onChange={(e) => setDialog(d => ({ ...d, limiteUsos: e.target.value }))}
                helperText="0 o vacío = ilimitado" inputProps={{ min: 0 }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Alcance</Typography>
              <Select fullWidth size="small" value={dialog?.alcance || 'todos'}
                onChange={(e) => setDialog(d => ({ ...d, alcance: e.target.value }))}>
                <MenuItem value="todos">Todos los servicios</MenuItem>
                <MenuItem value="categorias">Categorías específicas</MenuItem>
              </Select>
            </Grid>

            {dialog?.alcance === 'categorias' && (
              <Grid size={12}>
                <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Categorías donde aplica</Typography>
                <Select multiple fullWidth size="small" displayEmpty error={!!errores.alcance}
                  value={dialog?.categorias || []}
                  onChange={(e) => setDialog(d => ({ ...d, categorias: e.target.value }))}
                  renderValue={(sel) => sel.length ? sel.map(catNombre).filter(Boolean).join(', ') : 'Selecciona categorías…'}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  sx={{ color: (dialog?.categorias || []).length ? 'inherit' : '#94A3B8' }}>
                  {categoriasLista.map(c => (
                    <MenuItem key={c._id} value={c._id}>
                      <Checkbox size="small" checked={(dialog?.categorias || []).includes(c._id)} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
                </Select>
                {errores.alcance && <Typography variant="caption" color="#B23B33">{errores.alcance}</Typography>}
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 700, mb: 0.5 }}>Vigencia (opcional)</Typography>
              <TextField fullWidth size="small" type="date" value={dialog?.vigencia || ''}
                onChange={(e) => setDialog(d => ({ ...d, vigencia: e.target.value }))}
                InputLabelProps={{ shrink: true }} helperText="Vacío = sin vencimiento" />
            </Grid>

            <Grid size={12}>
              <TextField label="Descripción (opcional)" fullWidth size="small" value={dialog?.descripcion || ''}
                onChange={(e) => setDialog(d => ({ ...d, descripcion: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="#0E2465" fontWeight={600}>Activo</Typography>
                <Switch checked={!!dialog?.activo} onChange={(e) => setDialog(d => ({ ...d, activo: e.target.checked }))} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none', color: '#737791' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}
            sx={{ bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}>
            {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminar */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#FFE0DD', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <DeleteOutlineOutlinedIcon sx={{ color: '#B23B33' }} />
          </Box>
          <Typography variant="h6" fontWeight={800} color="#0E2465" mb={1}>¿Eliminar código?</Typography>
          <Typography variant="body2" color="#737791" mb={3}>
            El código <strong>{confirmDel?.codigo}</strong> dejará de funcionar. Las reservas que ya lo usaron no se ven afectadas.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="outlined" onClick={() => setConfirmDel(null)} sx={{ textTransform: 'none', borderRadius: 2, color: '#737791', borderColor: '#E2E8F0' }}>Cancelar</Button>
            <Button variant="contained" onClick={eliminar} sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#B23B33', '&:hover': { bgcolor: '#8F2E28' } }}>Eliminar</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.tipo} onClose={() => setToast(null)} sx={{ borderRadius: 2 }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Promociones;
